/**
 * Shape route state for create/update API payloads (not client-side validation).
 */

/** DTMF keys used for ivrMenu.options / offHoursIvrMenu.options (matches Lambda lookup). */
export const IVR_MENU_DIGIT_ORDER = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "*", "#"];

export function hasActiveCalendarDays(calendar) {
  if (!calendar || typeof calendar !== "object") return false;
  return Object.values(calendar).some((day) => day?.active === true);
}

/** Per-option BH: explicit flag, or active days on the option's own calendar. */
export function resolveOptionBusinessHoursEnabled(opt) {
  if (!opt || typeof opt !== "object") return false;
  if (opt.businessHoursEnabled === true) return true;
  if (opt.businessHoursEnabled === false) return false;
  return hasActiveCalendarDays(opt.calendar);
}

/**
 * Resolve the DTMF digit for an IVR option (object key must match this value in API/Lambda).
 * @param {object} opt
 * @param {string|null} legacyKey - Previous map key (may be 0-based index)
 * @param {number} index - Position when iterating options
 */
export function resolveOptionDigit(opt, legacyKey = null, index = 0) {
  if (!opt || typeof opt !== "object") return String(index + 1);
  if (opt.digit != null && opt.digit !== "") return String(opt.digit);
  const nameMatch = opt.name && String(opt.name).match(/Press\s+(.)/i);
  if (nameMatch) return nameMatch[1];
  const idMatch = opt.id && String(opt.id).match(/^press_(.+)$/i);
  if (idMatch) return idMatch[1];
  if (legacyKey != null && IVR_MENU_DIGIT_ORDER.includes(String(legacyKey))) {
    return String(legacyKey);
  }
  if (legacyKey != null && /^\d+$/.test(String(legacyKey))) {
    return String(Number(legacyKey) + 1);
  }
  return String(index + 1);
}

export function sortIvrOptionEntries(entries) {
  const rank = (key) => {
    const i = IVR_MENU_DIGIT_ORDER.indexOf(String(key));
    return i === -1 ? 999 : i;
  };
  return [...entries].sort(([a], [b]) => rank(a) - rank(b));
}

/** Collect digits already taken (by map key or option digit/id/name — includes legacy array / index keys). */
export function getUsedIvrDigits(options) {
  const used = new Set();
  if (!options || typeof options !== "object") return used;
  const entries = Array.isArray(options)
    ? options.map((opt, idx) => [String(idx), opt])
    : Object.entries(options);
  entries.forEach(([legacyKey, opt], index) => {
    used.add(resolveOptionDigit(opt, legacyKey, index));
  });
  return used;
}

/** First unused DTMF digit for a new menu option. */
export function getNextAvailableIvrDigit(options) {
  const used = getUsedIvrDigits(options);
  for (const d of IVR_MENU_DIGIT_ORDER) {
    if (!used.has(d)) return d;
  }
  return null;
}

/**
 * Re-key options by DTMF digit. Handles legacy 0-based index keys and DynamoDB-wrapped maps.
 */
export function normalizeIvrOptionsMap(options) {
  if (!options) return options;

  let raw = options;
  if (raw.M && typeof raw.M === "object" && !Array.isArray(raw.M)) {
    raw = raw.M;
  }

  const entries = Array.isArray(raw)
    ? raw.map((opt, idx) => [String(idx), opt])
    : sortIvrOptionEntries(Object.entries(raw));

  const result = {};
  let index = 0;
  for (const [legacyKey, opt] of entries) {
    if (!opt || typeof opt !== "object") {
      index += 1;
      continue;
    }
    const unwrapped = opt.M && typeof opt.M === "object" ? opt.M : opt;
    const digit = resolveOptionDigit(unwrapped, legacyKey, index);
    index += 1;
    if (result[digit]) continue;
    result[digit] = {
      ...unwrapped,
      digit,
      id: unwrapped.id || `press_${digit}`,
      name: unwrapped.name || `Press ${digit}`,
    };
  }
  return result;
}

/** Apply digit-keyed options to ivrMenu / offHoursIvrMenu on the route before save. */
export function normalizeRouteIvrMenusForApi(route) {
  const r = { ...route };
  if (r.ivrMenu?.options) {
    r.ivrMenu = {
      ...omitIvrMenuBusinessHoursField(r.ivrMenu),
      options: normalizeIvrOptionsMap(r.ivrMenu.options),
    };
  }
  if (r.offHoursIvrMenu?.options) {
    r.offHoursIvrMenu = {
      ...omitIvrMenuBusinessHoursField(r.offHoursIvrMenu),
      options: normalizeIvrOptionsMap(r.offHoursIvrMenu.options),
    };
  }
  return r;
}

/** IVR menu objects do not carry businessHoursEnabled; only options do. */
export function omitIvrMenuBusinessHoursField(menu) {
  if (!menu || typeof menu !== "object") return menu;
  const { businessHoursEnabled: _omit, ...rest } = menu;
  return rest;
}

/** Coerce businessHoursEnabled before API save. Route-level flag only; IVR BH lives on options only. */
export function normalizeRouteBusinessHoursForApi(route, enableBusinessHours) {
  const r = { ...route };
  r.businessHoursEnabled = !!enableBusinessHours;

  const normalizeMenu = (menu) => {
    if (!menu || typeof menu !== "object") return menu;
    const menuRest = omitIvrMenuBusinessHoursField(menu);
    const optionsRaw =
      menuRest.options && typeof menuRest.options === "object" && !Array.isArray(menuRest.options)
        ? normalizeIvrOptionsMap(menuRest.options)
        : menuRest.options;
    const options =
      optionsRaw && typeof optionsRaw === "object" && !Array.isArray(optionsRaw)
        ? Object.fromEntries(
            Object.entries(optionsRaw).map(([k, opt]) => [
              k,
              opt && typeof opt === "object"
                ? { ...opt, businessHoursEnabled: resolveOptionBusinessHoursEnabled(opt) }
                : opt,
            ])
          )
        : optionsRaw;
    return options ? { ...menuRest, options } : menuRest;
  };

  if (r.ivrMenu) r.ivrMenu = normalizeMenu(r.ivrMenu);
  if (r.offHoursIvrMenu) r.offHoursIvrMenu = normalizeMenu(r.offHoursIvrMenu);

  return r;
}
